function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState, useMemo, useContext, useRef, useLayoutEffect } from "react";
import classnames from "classnames";
import { BUCKET_OPTIONS, UNIT_OF_MEASURE } from "segments/desktop/pricing/constants";
import productPricingConfig from "segments/desktop/pricing/product-pricing-config";
import CarouselGallary from "/components/core/carousel-gallary/carousel-gallary";
import { trackEvent } from "services/bigbrain-service";
import { PRICING_PAGE_PURCHASE_NOW_CLICKED, PRICING_PAGE_SEAT_BUCKET_CHANGED, PRICING_PAGE_PERIOD_CHANGED, PRICING_PAGE_BUCKET_AMOUNT_CHANGED } from "constants/bigbrain-event-types";
import { storeNumberOfContacts, storeNumberOfSeats } from "services/sales-enrichment/sales-enrichment-service";
import { TIERS } from "constants/pricing";
import { getPreviousTierNameByTier } from "services/pricing/plans-service";
import ProductPriceAndFeaturesCard from "./product-price-and-features-card/product-price-and-features-card";
import { UsersSelect } from "./users-select/users-select-component";
import { PeriodSelect } from "./period-select/period-select-component";
import PurchaseNowModal from "../components/purchase-now-modal/purchase-now-modal";
import { getMostPopularTierAndText } from "/services/pricing/pricing-page/most-popular-service";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { getCurrencyName } from "/services/pricing-service";
import AddonBanner from "../../addons/banner/index.js";
import useAddon from "../../addons/use-addon";
import { StyledProductPriceAndFeaturesCards } from "./product-prices-and-features-cards-component.scss";
const useMaxScrollHeightSync = (deps)=>{
    const refs = useRef(new Map());
    const refSetters = useRef(new Map());
    const scheduleMeasureRef = useRef(()=>{});
    const [maxHeight, setMaxHeight] = useState(0);
    const setRef = useCallback((key)=>{
        if (!refSetters.current.has(key)) {
            refSetters.current.set(key, (el)=>{
                if (el) refs.current.set(key, el);
                else refs.current.delete(key);
            });
        }
        return refSetters.current.get(key);
    }, []);
    const requestMeasure = useCallback(()=>scheduleMeasureRef.current(), []);
    useLayoutEffect(()=>{
        if (typeof window === "undefined") return undefined;
        const measure = ()=>{
            const elements = Array.from(refs.current.values());
            if (elements.length === 0) return;
            // Temporarily clear inline min-height so scrollHeight reflects natural content size
            // (after viewport widening, content can shrink below the previously-applied min-height)
            const previousMinHeights = elements.map((el)=>el.style.minHeight);
            elements.forEach((el)=>{
                el.style.minHeight = "0px";
            });
            const max = Math.max(...elements.map((el)=>el.scrollHeight));
            elements.forEach((el, i)=>{
                el.style.minHeight = previousMinHeights[i];
            });
            setMaxHeight((prev)=>prev !== max ? max : prev);
        };
        measure();
        let rafId = null;
        const scheduleMeasure = ()=>{
            if (rafId) return;
            rafId = window.requestAnimationFrame(()=>{
                rafId = null;
                measure();
            });
        };
        scheduleMeasureRef.current = scheduleMeasure;
        window.addEventListener("resize", scheduleMeasure);
        let observer;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(scheduleMeasure);
            refs.current.forEach((el)=>observer.observe(el));
        }
        return ()=>{
            if (rafId) window.cancelAnimationFrame(rafId);
            window.removeEventListener("resize", scheduleMeasure);
            if (observer) observer.disconnect();
            scheduleMeasureRef.current = ()=>{};
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return {
        setRef,
        maxHeight,
        requestMeasure
    };
};
const ProductPriceAndFeaturesCards = ({ productName, hideFreeTier, hideFreeForever, translateOrTranslateWithParam, isHomepagePricing, cookies, forcePricing, abTests, hasFreeTier, isApplyMaxDiffFeatures, defaultSeatsIndex, pricesAndFeaturesCardsPricing, isNewPricing, shouldHideBasicTier, shouldHideSeatsSelection, unitOfMeasure, crossedPricingUI, useClassicCardChrome, creditsPerSeat, addon })=>{
    const { translate, currency: currencyFromContext, forceCurrency, cloudfrontViewerCountry } = useContext(GenericConfigContext);
    const currency = currencyFromContext || getCurrencyName({
        forceCurrency,
        countryCode: cloudfrontViewerCountry
    });
    const { tiers, color, colorMobile, ctaHoverBackgroundColor, ctaHoverBorderColor, ctaHoverTextColor, hideProductIcon, primaryColor, secondaryColor, productId, showLearnMoreVideo, showFreeTierComingSoon, labeledTierFeatures } = productPricingConfig[productName];
    const { color: coreColor } = productPricingConfig.core;
    const SeatsOptionsIndex = defaultSeatsIndex ? defaultSeatsIndex : 0;
    const optionsIndex = unitOfMeasure === UNIT_OF_MEASURE.CONTACTS ? 0 : SeatsOptionsIndex;
    const options = BUCKET_OPTIONS[unitOfMeasure] || BUCKET_OPTIONS[UNIT_OF_MEASURE.SEATS];
    const [bucketAmount, setBucketAmount] = useState(Object.values(options)[optionsIndex]);
    const [period, setPeriod] = useState("yearly");
    const [purchaseNowModal, setPurchaseNowModal] = useState(false);
    const [purchaseModalTarget, setPurchaseModalTarget] = useState(null);
    const [addonUnit, setAddonUnit, addonTotalPrice] = useAddon(addon, currency, period);
    const onAddonUnitChange = useCallback((newUnit)=>{
        setAddonUnit(newUnit);
    }, [
        setAddonUnit
    ]);
    const onPurchaseNowClicked = ({ tier, target })=>{
        trackEvent(PRICING_PAGE_PURCHASE_NOW_CLICKED, {
            kind: productName,
            info1: tier.name,
            info2: period,
            info3: bucketAmount,
            productKind: productName
        });
        setPurchaseNowModal(true);
        setPurchaseModalTarget(target);
    };
    const setSelectedBucketAmount = useCallback((newSelectedBucketAmount)=>{
        if (unitOfMeasure === UNIT_OF_MEASURE.CONTACTS) {
            storeNumberOfContacts(newSelectedBucketAmount);
            trackEvent(PRICING_PAGE_BUCKET_AMOUNT_CHANGED, {
                info1: newSelectedBucketAmount,
                info2: bucketAmount,
                productKind: productName
            });
        } else {
            storeNumberOfSeats(newSelectedBucketAmount);
            trackEvent(PRICING_PAGE_SEAT_BUCKET_CHANGED, {
                info1: newSelectedBucketAmount,
                info2: bucketAmount,
                productKind: productName
            });
        }
        setBucketAmount(newSelectedBucketAmount);
    }, [
        productName,
        bucketAmount,
        unitOfMeasure
    ]);
    const setSelectedPeriod = useCallback((newSelectedPeriod)=>{
        trackEvent(PRICING_PAGE_PERIOD_CHANGED, {
            info1: newSelectedPeriod,
            info2: period,
            productKind: productName
        });
        setPeriod(newSelectedPeriod);
    }, [
        period,
        productName
    ]);
    const getTiers = useCallback(()=>{
        // hasFreeTier is cookie monday_has_free_tier based
        // hideFreeTier is a prop passed from the generator
        // free tier will be hidden if hasFreeTier is false OR if hasFreeTier AND hideFreeTier are true
        // TODO: extract to util function or custom hook
        if (!hasFreeTier || hideFreeTier) {
            return tiers.filter((tier)=>tier.name !== TIERS.FREE);
        }
        return tiers;
    }, [
        tiers,
        hideFreeTier,
        hasFreeTier
    ]);
    const getConditionalFeatures = useCallback((tier)=>{
        // If this is the STANDARD tier and shouldHideBasicTier is true, use alternative features
        if (tier.name === TIERS.STANDARD && shouldHideBasicTier) {
            return [
                "ai_credits",
                "sidekick_ai_assistant",
                "unlimitedItems",
                "unlimitedBoards",
                "guestAccess",
                "automations250",
                "integrations250",
                "over20ColumnTypes",
                "dashboardCombined5boards",
                "timelineAndGanttViews"
            ];
        }
        // Otherwise return the tier's original features
        return tier.features;
    }, [
        shouldHideBasicTier
    ]);
    const getPreviousTierName = (tier)=>{
        return getPreviousTierNameByTier(tier, filteredTiers);
    };
    const mostPopularTier = useMemo(()=>{
        const relevantTiers = getTiers();
        if (!relevantTiers) return;
        const modifiedTiers = relevantTiers.map((tier)=>_object_spread_props(_object_spread({}, tier), {
                tier: tier.name
            }));
        const { tier: mostPopularTierName } = getMostPopularTierAndText(bucketAmount, modifiedTiers, null, abTests, cookies, forcePricing, unitOfMeasure);
        return mostPopularTierName;
    }, [
        abTests,
        cookies,
        forcePricing,
        bucketAmount,
        tiers
    ]);
    const filteredTiers = shouldHideBasicTier ? getTiers().filter((tier)=>tier.name !== TIERS.BASIC) : getTiers();
    const tiersKey = filteredTiers.map((t)=>t.name).join(",");
    // New pricing always highlights Pro as "most popular" unless the upstream
    // pick is Enterprise; matches the same logic used for `isMostPopular` below.
    // Mobile carousel uses this to default the visible slide to the popular tier.
    const mostPopularTierName = isNewPricing && mostPopularTier !== TIERS.ENTERPRISE ? TIERS.PRO : mostPopularTier;
    // findIndex returns -1 if the tier was filtered out (e.g. shouldHideBasicTier
    // removes BASIC). Math.max guards that case — falls back to first slide.
    const mostPopularTierIndex = Math.max(0, filteredTiers.findIndex((tier)=>tier.name === mostPopularTierName));
    // Desktop and mobile lists each render their own copy of the cards, so they
    // need independent ref maps — sharing one would let mobile (display:none,
    // scrollHeight 0) overwrite desktop entries and pin the max at 0.
    const { setRef: setDesktopCreditsRef, maxHeight: maxDesktopCreditsHeight } = useMaxScrollHeightSync([
        period,
        bucketAmount,
        tiersKey
    ]);
    const { setRef: setMobileCreditsRef, maxHeight: maxMobileCreditsHeight } = useMaxScrollHeightSync([
        period,
        bucketAmount,
        tiersKey
    ]);
    const { setRef: setMobileCardRef, maxHeight: maxMobileCardHeight, requestMeasure: requestMobileCardMeasure } = useMaxScrollHeightSync([
        period,
        bucketAmount,
        tiersKey
    ]);
    // The AI block uses a grid-template-rows transition to expand/collapse. Its
    // intermediate sizes don't always trip the cards' ResizeObserver (the card
    // can stay clamped to min-height during the early/late frames), so when the
    // transition lands we re-measure to make the slider-list catch up.
    const handleMobileCardsTransitionEnd = useCallback((e)=>{
        if (e.propertyName === "grid-template-rows") requestMobileCardMeasure();
    }, [
        requestMobileCardMeasure
    ]);
    // nuka-carousel sets .slider-list height once and never re-measures when
    // our cards grow. Pin .slider-list to fit the popular tier's wrapper
    // (which extends below others by banner + .card margin = 48px) plus the
    // wrapper's own margin-top and the bottom dots area.
    // Scoped to this instance via mobileTiersRef — page may have multiple
    // ProductPriceAndFeaturesCards (per product), so document.querySelector
    // would only target the first one and skew sibling carousels.
    const mobileTiersRef = useRef(null);
    useLayoutEffect(()=>{
        if (typeof window === "undefined" || !maxMobileCardHeight) return;
        const root = mobileTiersRef.current;
        if (!root) return;
        const sliderList = root.querySelector(".slider-list");
        if (!sliderList) return;
        const wrapper = root.querySelector(".card-container");
        const wrapperMarginTop = wrapper ? parseFloat(window.getComputedStyle(wrapper).marginTop) || 0 : 0;
        const dots = root.querySelector(".slider-control-bottomcenter");
        const dotsHeight = dots ? dots.offsetHeight : 0;
        // +44 matches the bumped .card min-height (extra padding-bottom),
        // +24 gives visual breathing room between cards' bottoms and the dots.
        sliderList.style.height = `${maxMobileCardHeight + 44 + wrapperMarginTop + dotsHeight + 24}px`;
    }, [
        maxMobileCardHeight
    ]);
    return /*#__PURE__*/ _jsxs(StyledProductPriceAndFeaturesCards, {
        numberOfTiers: filteredTiers.length,
        className: classnames("container", {
            "is-homepage-pricing": isHomepagePricing
        }),
        isNewPricing: isNewPricing,
        children: [
            /*#__PURE__*/ _jsx(AddonBanner, {
                addon: addon,
                selectedUnit: addonUnit,
                onUnitChange: onAddonUnitChange,
                period: period
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "seats-and-period-selection",
                children: [
                    !shouldHideSeatsSelection ? /*#__PURE__*/ _jsx(UsersSelect, {
                        usersCountArray: options,
                        updateSelectedUserCountCallback: setSelectedBucketAmount,
                        productName: productName,
                        defaultSeatsIndex: optionsIndex,
                        unitOfMeasure: unitOfMeasure
                    }) : /*#__PURE__*/ _jsx("div", {}),
                    /*#__PURE__*/ _jsx(PeriodSelect, {
                        color: color,
                        isHomepagePricing: isHomepagePricing,
                        primaryColor: primaryColor,
                        period: period,
                        onPeriodChange: setSelectedPeriod,
                        crossedPricingUI: crossedPricingUI,
                        useClassicCardChrome: useClassicCardChrome
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "tiers",
                children: filteredTiers.map((tier)=>{
                    const tierWithConditionalFeatures = _object_spread_props(_object_spread({}, tier), {
                        features: getConditionalFeatures(tier)
                    });
                    return /*#__PURE__*/ _jsx(ProductPriceAndFeaturesCard, {
                        period: period,
                        hideProductIcon: hideProductIcon,
                        productName: productName,
                        tier: tierWithConditionalFeatures,
                        tiers: getTiers(),
                        previousTierName: getPreviousTierName(tier),
                        color: color,
                        colorMobile: colorMobile,
                        ctaHoverBackgroundColor: ctaHoverBackgroundColor,
                        ctaHoverBorderColor: ctaHoverBorderColor,
                        ctaHoverTextColor: ctaHoverTextColor,
                        primaryColor: primaryColor,
                        secondaryColor: secondaryColor,
                        seats: bucketAmount,
                        coreColor: coreColor,
                        isMostPopular: isNewPricing && mostPopularTier !== TIERS.ENTERPRISE ? tier.name === TIERS.PRO : mostPopularTier === tier.name,
                        onPurchaseNowClicked: onPurchaseNowClicked,
                        hideFreeForever: hideFreeForever,
                        isHomepagePricing: isHomepagePricing,
                        productId: productId,
                        showLearnMoreVideo: showLearnMoreVideo,
                        showFreeTierComingSoon: showFreeTierComingSoon,
                        isApplyMaxDiffFeatures: isApplyMaxDiffFeatures,
                        labeledTierFeatures: labeledTierFeatures,
                        isNewPricing: isNewPricing,
                        unitOfMeasure: unitOfMeasure,
                        crossedPricingUI: crossedPricingUI,
                        useClassicCardChrome: useClassicCardChrome,
                        creditsPerSeat: creditsPerSeat,
                        addon: addon,
                        selectedUnit: addonUnit,
                        addonTotalPrice: addonTotalPrice,
                        creditsWrapperRef: setDesktopCreditsRef(tier.name),
                        minCreditsWrapperHeight: maxDesktopCreditsHeight
                    }, tier.name);
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "tiers tiers-mobile",
                ref: mobileTiersRef,
                onTransitionEnd: handleMobileCardsTransitionEnd,
                children: /*#__PURE__*/ _jsx(CarouselGallary, {
                    className: "carousel-item",
                    autoplay: false,
                    wrapAround: false,
                    edgeEasing: "easeBack",
                    slidesToShow: 1,
                    slideIndex: mostPopularTierIndex,
                    cellAlign: "center",
                    cellSpacing: 16,
                    width: "100vw",
                    slideWidth: 0.7,
                    renderCenterLeftControls: ()=>{},
                    renderCenterRightControls: ()=>{},
                    heightMode: "max",
                    children: getTiers().map((tier)=>{
                        const tierWithConditionalFeatures = _object_spread_props(_object_spread({}, tier), {
                            features: getConditionalFeatures(tier)
                        });
                        return /*#__PURE__*/ _jsx(ProductPriceAndFeaturesCard, {
                            period: period,
                            hideProductIcon: hideProductIcon,
                            productName: productName,
                            tier: tierWithConditionalFeatures,
                            tiers: getTiers(),
                            previousTierName: getPreviousTierName(tier),
                            color: color,
                            colorMobile: colorMobile,
                            ctaHoverBackgroundColor: ctaHoverBackgroundColor,
                            ctaHoverBorderColor: ctaHoverBorderColor,
                            ctaHoverTextColor: ctaHoverTextColor,
                            primaryColor: primaryColor,
                            secondaryColor: secondaryColor,
                            seats: bucketAmount,
                            coreColor: coreColor,
                            isMostPopular: isNewPricing && mostPopularTier !== TIERS.ENTERPRISE ? tier.name === TIERS.PRO : mostPopularTier === tier.name,
                            onPurchaseNowClicked: onPurchaseNowClicked,
                            hideFreeForever: hideFreeForever,
                            isHomepagePricing: isHomepagePricing,
                            productId: productId,
                            showLearnMoreVideo: showLearnMoreVideo,
                            showFreeTierComingSoon: showFreeTierComingSoon,
                            isApplyMaxDiffFeatures: isApplyMaxDiffFeatures,
                            labeledTierFeatures: labeledTierFeatures,
                            isNewPricing: isNewPricing,
                            unitOfMeasure: unitOfMeasure,
                            crossedPricingUI: crossedPricingUI,
                            useClassicCardChrome: useClassicCardChrome,
                            creditsPerSeat: creditsPerSeat,
                            addon: addon,
                            selectedUnit: addonUnit,
                            addonTotalPrice: addonTotalPrice,
                            creditsWrapperRef: setMobileCreditsRef(tier.name),
                            minCreditsWrapperHeight: maxMobileCreditsHeight,
                            mobileCardWrapperRef: setMobileCardRef(tier.name),
                            minMobileCardHeight: maxMobileCardHeight
                        }, tier.name);
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "pricing-tax-disclaimer",
                children: translate("pricingPage.taxDisclaimer")
            }),
            /*#__PURE__*/ _jsx(PurchaseNowModal, {
                isOpen: purchaseNowModal,
                signupButton: purchaseModalTarget
            })
        ]
    });
};
export default ProductPriceAndFeaturesCards;
